/**
 * Medication ID Parser Utility
 * Parses medication selection keys into their components
 */

export interface ParsedMedicationId {
  itemType: "vaccine" | "medication";
  protocolId: string;
  isManual: boolean;
  isNotRecommended: boolean;
}

export type ApiSourceType = "pharmacist_added" | "not_recommended" | undefined;

/**
 * Parse a medication selection key into its components
 *
 * Key formats:
 * - "ai-vaccine-{protocol_id}" - AI recommended vaccine
 * - "ai-medication-{protocol_id}" - AI recommended medication
 * - "manual-vaccine-{protocol_id}" - Pharmacist added vaccine
 * - "manual-medication-{protocol_id}" - Pharmacist added medication
 * - "not-rec-vaccine-{protocol_id}" - Not recommended vaccine
 * - "not-rec-medication-{protocol_id}" - Not recommended medication
 * - "vaccine-{protocol_id}" - Legacy format
 * - "medication-{protocol_id}" - Legacy format
 */
export function parseMedicationId(medicationId: string): ParsedMedicationId | null {
  let itemType: "vaccine" | "medication";
  let protocolId: string;
  let isManual = false;
  let isNotRecommended = false;

  if (medicationId.startsWith("manual-vaccine-")) {
    itemType = "vaccine";
    protocolId = medicationId.replace("manual-vaccine-", "");
    isManual = true;
  } else if (medicationId.startsWith("manual-medication-")) {
    itemType = "medication";
    protocolId = medicationId.replace("manual-medication-", "");
    isManual = true;
  } else if (medicationId.startsWith("ai-vaccine-")) {
    itemType = "vaccine";
    protocolId = medicationId.replace("ai-vaccine-", "");
  } else if (medicationId.startsWith("ai-medication-")) {
    itemType = "medication";
    protocolId = medicationId.replace("ai-medication-", "");
  } else if (medicationId.startsWith("not-rec-vaccine-")) {
    itemType = "vaccine";
    protocolId = medicationId.replace("not-rec-vaccine-", "");
    isNotRecommended = true;
  } else if (medicationId.startsWith("not-rec-medication-")) {
    itemType = "medication";
    protocolId = medicationId.replace("not-rec-medication-", "");
    isNotRecommended = true;
  } else if (medicationId.startsWith("vaccine-")) {
    itemType = "vaccine";
    protocolId = medicationId.replace("vaccine-", "");
  } else if (medicationId.startsWith("medication-")) {
    itemType = "medication";
    protocolId = medicationId.replace("medication-", "");
  } else {
    return null;
  }

  return {
    itemType,
    protocolId,
    isManual,
    isNotRecommended,
  };
}

/**
 * Get the API source value based on parsed medication ID
 */
export function getApiSource(parsed: ParsedMedicationId): ApiSourceType {
  if (parsed.isManual) {
    return "pharmacist_added";
  } else if (parsed.isNotRecommended) {
    return "not_recommended";
  }
  return undefined;
}
